import { ActivityEntry, getActivityDomainLabel } from "src/lib/activityService";
import { ACTION_STYLES } from "./Activity";
import { ChevronDown, ChevronRight } from "lucide-react";

type ActivityEntryListItemProps = {
  entry: ActivityEntry;
  expanded: boolean;
  onToggle: () => void;
};

function getActionClassName(action: string) {
  return ACTION_STYLES[action] ?? "border-white/15 bg-white/10 text-gray-300";
}

function hasDetails(entry: ActivityEntry) {
  return entry.details != null && Object.keys(entry.details).length > 0;
}

const ActivityEntryListItem: React.FC<ActivityEntryListItemProps> = ({
  entry,
  expanded,
  onToggle,
}) => {
  const actionClassName = getActionClassName(entry.action);
  const showDetails = expanded && hasDetails(entry);

  return (
    <li className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04]">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/[0.04]"
        aria-expanded={expanded}
      >
        <span className="mt-0.5 shrink-0 text-gray-500">
          {expanded ? (
            <ChevronDown className="h-4 w-4" aria-hidden />
          ) : (
            <ChevronRight className="h-4 w-4" aria-hidden />
          )}
        </span>

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex min-w-0 flex-wrap items-center gap-1.5">
            <span className="rounded-md border border-white/10 bg-white/5 px-1.5 py-0.5 text-[11px] font-bold text-gray-300">
              {getActivityDomainLabel(entry.domain)}
            </span>

            <span
              className={[
                "rounded-md border px-1.5 py-0.5 text-[11px] font-bold capitalize",
                actionClassName,
              ].join(" ")}
            >
              {entry.action}
            </span>

            <span className="min-w-0 truncate font-mono text-[11px] text-gray-500">
              {entry.resourceType}:{entry.resourceId}
            </span>
          </div>

          <p className="truncate text-sm font-semibold text-white">
            {entry.summary}
          </p>

          <p className="mt-0.5 truncate text-xs text-gray-500">
            {entry.actor.displayName ?? entry.actor.email}
            <span className="text-gray-600"> · </span>
            {new Date(entry.ts).toLocaleString()}
          </p>
        </div>
      </button>

      {showDetails && (
        <div className="border-t border-white/5 px-3 pb-3 pl-10">
          <pre className="mt-2 max-h-64 overflow-auto rounded-xl bg-black/30 p-3 font-mono text-[11px] leading-relaxed text-gray-400">
            {JSON.stringify(entry.details, null, 2)}
          </pre>
        </div>
      )}
    </li>
  );
};

export default ActivityEntryListItem;
